import React, { useState } from 'react';
import { Box, Button, Alert, Link, CircularProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService, AuthApiError } from '../../services/auth.service';
import { AuthSplitLayout, authFooterSx } from './AuthSplitLayout';
import { AuthTextField } from './AuthTextField';
import { GoogleSignInButton } from './GoogleSignInButton';

export const LoginForm = () => {
  const { t } = useTranslation('auth');
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setUnverified(false);
    setResendMessage('');
    setSubmitting(true);
    try {
      await authService.login(form);
      // No need to store tokens - they're in httpOnly cookies
      window.location.href = '/';
    } catch (err) {
      if ((err as AuthApiError).code === 'EMAIL_NOT_VERIFIED') {
        setUnverified(true);
      } else {
        setError(t('login.invalidCredentials'));
      }
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    try {
      await authService.resendVerification(form.email);
      setResendMessage(t('verify.resent'));
    } catch {
      setResendMessage(t('verify.resendFailed'));
    }
  };

  return (
    <AuthSplitLayout title={t('login.title')} subtitle={t('login.subtitle')}>
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <AuthTextField
          label={t('fields.email')}
          type="email"
          autoComplete="email"
          placeholder={t('fields.emailPlaceholder')}
          required
          autoFocus
          value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
        />
        <AuthTextField
          label={t('fields.password')}
          type="password"
          autoComplete="current-password"
          required
          value={form.password}
          onChange={e => setForm({ ...form, password: e.target.value })}
          labelAction={
            <Link component={RouterLink} to="/forgot-password" underline="hover" sx={{ fontSize: 13 }}>
              {t('login.forgotPassword')}
            </Link>
          }
        />
        {error && <Alert severity="error">{error}</Alert>}
        {unverified && (
          <Alert
            severity="warning"
            action={!resendMessage && <Button color="inherit" size="small" onClick={handleResend}>{t('verify.resend')}</Button>}
          >
            {resendMessage || t('login.emailNotVerified')}
          </Alert>
        )}
        <Button fullWidth variant="contained" type="submit" disabled={submitting} sx={{ height: 42, mt: 0.5 }}>
          {submitting ? <CircularProgress size={18} sx={{ color: 'inherit' }} /> : t('login.submit')}
        </Button>
        <GoogleSignInButton mode="signin" />
        <Box sx={{ ...authFooterSx, mt: 1 }}>
          {t('login.noAccount')}{' '}
          <Link component={RouterLink} to="/register" underline="hover">{t('login.createOne')}</Link>
        </Box>
      </Box>
    </AuthSplitLayout>
  );
};
